/* eslint-disable @typescript-eslint/no-explicit-any */
// services/api.ts - FIXED VERSION

import type { Course, SchoolClass, FormCourse, FormClass, User, Announcement, AppBanner, AppRelease, MonthlyAnalytics, CertificateDTO, LessonPlayback, CourseMasterySummary, MasterySummary, WhitelistedEmailEntry } from '../types'
import { normalizeCourse, normalizeClass, normalizeCourseMasterySummary, normalizeMasterySummary } from '../types'

// Use environment variable for API URL (defaults to localhost for development)
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

// ─────────────────────────────────────────────────────────────
// Token management
// ─────────────────────────────────────────────────────────────

let accessToken: string | null = null
// The one refresh in flight. Every caller awaits this same promise, so they all get
// its real result (or its outage error), however long it takes.
let refreshInFlight: Promise<boolean> | null = null
// Upper bound on one auth request (/auth/refresh, /auth/me), headers and body together.
// A hung request ends as a NetworkError, never a hang.
export const AUTH_REQUEST_TIMEOUT_MS = 15_000
let tokenExpiryTimeout: ReturnType<typeof setTimeout> | null = null

const removeLegacyStorageKey = (key: string) => {
    if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(key)
    }
}

const setAccessToken = (token: string, expiryMs: number = 15 * 60 * 1000) => {
    if (tokenExpiryTimeout) {
        clearTimeout(tokenExpiryTimeout)
    }

    accessToken = token

    tokenExpiryTimeout = setTimeout(() => {
        accessToken = null
    }, expiryMs)
}

/**
 * Refresh token is stored as an HttpOnly cookie by the backend (not in localStorage).
 * This function is a no-op kept for call-site compatibility during migration.
 * It migrates any legacy localStorage token by removing it.
 */
const setRefreshToken = (_token: string) => {
    // Refresh token now lives exclusively in the backend HttpOnly cookie.
    // Remove any legacy value that may exist from a prior session.
    removeLegacyStorageKey('dhtn_refresh_token')
}

/** @deprecated Refresh token is HttpOnly cookie — not accessible from JS. Returns null. */
const getRefreshToken = (): string | null => null

const clearTokens = () => {
    accessToken = null
    if (tokenExpiryTimeout) {
        clearTimeout(tokenExpiryTimeout)
        tokenExpiryTimeout = null
    }
    // Remove legacy localStorage keys from before the HttpOnly-cookie migration
    removeLegacyStorageKey('dhtn_refresh_token')
    removeLegacyStorageKey('dhtn-auth')
}

/**
 * Refreshes the access token by calling /auth/refresh.
 * The HttpOnly refresh cookie is sent automatically via credentials: 'include'.
 * No refresh token is read from or written to localStorage.
 */
const refreshAccessToken = (): Promise<boolean> => {
    if (!refreshInFlight) {
        refreshInFlight = doRefresh().finally(() => { refreshInFlight = null })
    }
    return refreshInFlight
}

const networkError = () => {
    const netErr = new Error('Network error. Please check your connection.')
    netErr.name = 'NetworkError'
    return netErr
}

/**
 * fetch plus its body read, under one AUTH_REQUEST_TIMEOUT_MS bound. A dropped connection
 * or a timeout (before the headers or while the body streams) is a NetworkError; any other
 * error from `read` (e.g. bad JSON) is thrown as it is.
 */
export const fetchBounded = async <T>(url: string, init: RequestInit, read: (res: Response) => Promise<T>): Promise<T> => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS)
    try {
        let res: Response
        try {
            res = await fetch(url, { ...init, signal: controller.signal })
        } catch {
            throw networkError()
        }
        try {
            return await read(res)
        } catch (err) {
            if (controller.signal.aborted) throw networkError()
            throw err
        }
    } finally {
        clearTimeout(timeout)
    }
}

const doRefresh = async (): Promise<boolean> => {
    const { res, data } = await fetchBounded(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // HttpOnly refresh cookie travels automatically
        headers: { 'Content-Type': 'application/json' },
        // No body — refresh token is the HttpOnly cookie, not a body param
    }, async res => ({ res, data: res.ok ? await res.json() : null }))
    if (res.ok) {
        setAccessToken(data.accessToken)
        return true
    }
    if (res.status === 401 || res.status === 403) {
        // Genuinely invalid/expired refresh token — notify AuthContext so stale
        // `user` state (and any role-gated UI it lets through) is cleared, not
        // just the in-memory JWT.
        clearTokens()
        window.dispatchEvent(new CustomEvent('dhtn:auth-expired'))
        return false
    }
    // Outage or any other non-auth answer: the session may still be valid,
    // so leave it exactly as it is and report the real failure instead.
    const err: any = new Error(`Request failed with status ${res.status}`)
    err.status = res.status
    if (res.status >= 500) err.name = 'ServerError'
    throw err
}

const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    return headers
}

// Getter for access token (used by authContext)
const getAccessToken = (): string | null => accessToken

// ─────────────────────────────────────────────────────────────
// Core request handling
// ─────────────────────────────────────────────────────────────

const MAX_ERROR_MESSAGE_LENGTH = 200

const isPlainMessage = (value: unknown): value is string =>
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= MAX_ERROR_MESSAGE_LENGTH &&
    !/<[a-z!/]/i.test(value) // no HTML tags or doctype

type RequestOptions = RequestInit & { background?: boolean }

const handleResponse = async (response: Response, reportServerStatus = true): Promise<any> => {
    if (response.status === 401 && !response.url?.includes('/auth/')) {
        const refreshed = await refreshAccessToken()
        if (!refreshed) {
            const err = new Error('Session expired')
            err.name = 'AuthError'
            throw err
        }
        return null // signal retry
    }

    const text = await response.text()

    if (!response.ok) {
        let message = `Request failed with status ${response.status}`
        let data: any = null

        // err.message reaches admin screens and Sentry, so only keep a server
        // message that is short plain text. A 5xx must also be a JSON error from
        // our backend; a proxy's HTML page or a long body falls back to generic.
        try {
            if (text) {
                data = JSON.parse(text)
                const serverMessage = data?.message || data?.error
                if (isPlainMessage(serverMessage)) message = serverMessage
            }
        } catch {
            if (response.status < 500 && isPlainMessage(text)) message = text
        }

        const err: any = new Error(message)
        err.status = response.status
        err.responseData = data

        if (
            response.status === 402 ||
            message.toLowerCase().includes('payment') ||
            message.toLowerCase().includes('subscription')
        ) {
            err.name = 'SubscriptionRequiredError'
            throw err
        }

        if (message.toLowerCase().includes('already enrolled')) {
            err.name = 'AlreadyEnrolledError'
            throw err
        }

        if (response.status === 0 || response.status >= 500) {
            // 5xx means the server answered, so it is not "offline": keep the
            // status and a distinct name so classifyError can say "server error".
            err.name = response.status === 0 ? 'NetworkError' : 'ServerError'
            if (reportServerStatus) window.dispatchEvent(new CustomEvent('dhtn:server-error'))
        }

        throw err
    }

    if (reportServerStatus) window.dispatchEvent(new CustomEvent('dhtn:server-ok'))

    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {}
    }

    try {
        return text ? JSON.parse(text) : {}
    } catch {
        return {}
    }
}

const request = async <T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
    // Optional page data must not raise or clear a whole-app outage overlay.
    // Error classification, read retries and auth expiry remain unchanged.
    const { background = false, ...fetchOptions } = options
    const headers = { ...getAuthHeaders(), ...options.headers }
    // FormData uploads: let browser set Content-Type (with boundary) automatically
    if (options.body instanceof FormData) delete (headers as Record<string, string>)['Content-Type']
    try {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...fetchOptions,
            headers,
            credentials: 'include',
            mode: 'cors',
        })
        const result = await handleResponse(res, !background)
        if (result === null) return request<T>(endpoint, options) // retry after refresh
        return result
    } catch (err: any) {
        if (
            err.name === 'SubscriptionRequiredError' ||
            err.name === 'AlreadyEnrolledError' ||
            err.name === 'AuthError'
        )
            throw err

        // Pass 5xx through untouched so its status survives (enhancedRequest retries it).
        if (err.name === 'ServerError') throw err

        if (err.message?.includes('Failed to fetch') || err.name === 'NetworkError') {
            const netErr = new Error('Network error. Please check your connection.')
            netErr.name = 'NetworkError'
            throw netErr
        }

        throw err
    }
}

// Online-retry budget. We retry only when the device *thinks* it's online but a
// request still failed (a transient 2G/3G drop or a brief server hiccup). Two
// short backoffs (~3s worst case) ride out blips without leaving the user staring
// at a spinner. Truly-offline devices fail instantly below — they skip this.
const RETRY_DELAYS_MS = [1000, 2000]

const enhancedRequest = async <T = any>(
    endpoint: string,
    options: RequestOptions = {}
): Promise<T> => {
    // Fail fast when the device reports no connection at all. There is nothing to
    // retry into, so surface a NetworkError immediately instead of burning ~3s.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const offlineErr = new Error(
            'Unable to connect to server. Please check your internet connection.'
        )
        offlineErr.name = 'NetworkError'
        throw offlineErr
    }

    // Only reads are retried. A POST/PUT/PATCH/DELETE that failed may still have
    // reached the server, so replaying it could enrol, complete or pay twice.
    const method = (options.method ?? 'GET').toUpperCase()
    const delays = method === 'GET' || method === 'HEAD' ? RETRY_DELAYS_MS : []
    let lastError: unknown

    for (let attempt = 0; attempt <= delays.length; attempt++) {
        try {
            return await request<T>(endpoint, options)
        } catch (err: any) {
            if (
                err.name === 'SubscriptionRequiredError' ||
                err.name === 'AlreadyEnrolledError' ||
                err.name === 'AuthError'
            )
                throw err

            // 5xx: same retry budget as a network blip, but once it is spent throw
            // the real error (name 'ServerError' + status), never a fake "offline".
            // Checked before the 'Session expired' test so a 5xx body can never be
            // mistaken for an auth failure (it never was: it used to arrive renamed).
            if (err.name === 'ServerError') {
                lastError = err
                if (attempt < delays.length) {
                    await new Promise(r => setTimeout(r, delays[attempt]))
                    continue
                }
                throw err
            }

            if (err.message?.includes('Session expired')) {
                const authErr = new Error('Your session has expired. Please log in again.')
                authErr.name = 'AuthError'
                throw authErr
            }

            if (
                err.name === 'NetworkError' ||
                err.message?.includes('Network error') ||
                err.message?.includes('Failed to fetch')
            ) {
                lastError = err
                if (attempt < delays.length) {
                    await new Promise(r => setTimeout(r, delays[attempt]))
                    continue
                }
                const netErr = new Error(
                    'Unable to connect to server. Please check your internet connection.'
                )
                netErr.name = 'NetworkError'
                throw netErr
            }

            throw err
        }
    }

    throw lastError
}

// The web editor uses the same in-memory JWT, refresh and error handling as the app.
// Its narrow transport cannot call enrollment, mastery, progress or payment routes.
export const editorRequest = <T = unknown>(endpoint:string,options:RequestOptions={}) => {
    if(!/^\/admin\/(courses|classes|media|reviewer-notes)(\/|\?|$)/.test(endpoint))throw new Error('Unsupported editor endpoint')
    return enhancedRequest<T>(endpoint,options)
}

// ─────────────────────────────────────────────────────────────
// Mappers
// ─────────────────────────────────────────────────────────────

const mapCourse = (raw: any): Course =>
    normalizeCourse({
        ...raw,
        durationMinutes: raw.durationMinutes || 0,
        modules: raw.modules || [],
    })

const mapClass = (raw: any): SchoolClass => normalizeClass(raw)

// ─────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────

export const api = {
    postReviewerNote: (note: {classId:string;stageId?:string|null;lessonId?:string;anchor:Record<string,unknown>;note:string;app:'web'|'mobile';theme:'light'|'dark';screenWidth:number}) =>
        enhancedRequest('/reviewer-notes', {method:'POST',body:JSON.stringify(note)}),
    assignAdminReviewer: (email:string, note:string, classIds:string[]):Promise<WhitelistedEmailEntry> => enhancedRequest("/admin/whitelist/reviewers", {method:"POST",body:JSON.stringify({email,note,classIds})}),
    // ══════════════════════════════════════════════════════════
    // CLASSES — student-facing
    // ══════════════════════════════════════════════════════════

    getClasses: async (language?: string): Promise<SchoolClass[]> => {
        const query = language ? `?language=${language}` : ''
        const res = await enhancedRequest<any>(`/classes${query}`)
        const arr = Array.isArray(res) ? res : (res?.content ?? [])
        return arr.map(mapClass)
    },

    joinWaitlist: async (email: string, language: string): Promise<void> => {
        await enhancedRequest<any>('/waitlist', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, language }),
        })
    },

    notifyMeForClass: async (email: string, classId: string): Promise<void> => {
        await enhancedRequest<any>('/waitlist', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, classId }),
        })
    },

    getWaitlistCount: async (): Promise<number> => {
        const res = await enhancedRequest<{ count: number }>('/waitlist/count')
        return res?.count ?? 0
    },

    getAdminWaitlist: async (classId?: string): Promise<{ email: string; createdAt: string }[]> => {
        const url = classId ? `/admin/waitlist?classId=${encodeURIComponent(classId)}` : '/admin/waitlist'
        return await enhancedRequest<{ email: string; createdAt: string }[]>(url) ?? []
    },

    // outlines: also return unpublished courses as "coming soon" outlines (title and module titles,
    // no lessons). Only the class page asks for them; every other caller keeps the old payload.
    getClassById: async (id: string, options: { background?: boolean; outlines?: boolean } = {}): Promise<SchoolClass> => {
        const { outlines, ...request } = options
        const timestamp = Date.now()
        const res = await enhancedRequest<any>(`/classes/${id}?_t=${timestamp}${outlines ? '&outlines=1' : ''}`, request)
        return mapClass(res)
    },

    getEnrolledClasses: async (): Promise<SchoolClass[]> => {
        const res = await enhancedRequest<any>('/classes/enrolled')
        const arr = Array.isArray(res) ? res : (res?.content ?? [])
        return arr.map(mapClass)
    },

    enrollInClass: async (classId: string): Promise<any> => {
        try {
            return await enhancedRequest(`/classes/${classId}/enroll`, { method: 'POST' })
        } catch (err: any) {
            if (!err.classId) err.classId = classId
            throw err
        }
    },

    completeLesson: async (classId: string, lessonId: string, stageId: string | null, timeSpentSeconds?: number): Promise<{ courseComplete: boolean; stageComplete: boolean; classComplete: boolean }> => {
        const res = await enhancedRequest<unknown>(`/classes/${classId}/lessons/${lessonId}/complete${stageId ? `?stageId=${stageId}` : ''}`, {
            method: 'POST',
            body: timeSpentSeconds !== undefined ? JSON.stringify({ timeSpentSeconds }) : undefined,
        }) as Record<string, unknown>
        return {
            courseComplete: res?.courseComplete === true,
            stageComplete: res?.stageComplete === true,
            classComplete: res?.classComplete === true,
        }
    },

    submitMasteryAttempt: async (
        classId: string,
        lessonId: string,
        stageId: string | null,
        body: {
            correctItems: number
            totalItems: number
            passingScorePercent?: number
            timeSpentSeconds?: number
            quizAnswers?: Array<{
                questionIndex: number
                selectedOptionIndices: number[]
            }>
            practiceAnswers?: Array<{
                exerciseIndex: number
                answer?: string
                pairs?: Record<string, string>
            }>
        }
    ): Promise<{
        attemptId: number
        scorePercent: number
        passingScorePercent: number
        passed: boolean
        attemptNumber: number
        bestScorePercent: number
        masteryPassed: boolean
    }> => {
        const res = await enhancedRequest<any>(`/classes/${classId}/lessons/${lessonId}/mastery/attempts${stageId ? `?stageId=${stageId}` : ''}`, {
            method: 'POST',
            body: JSON.stringify(body),
        })
        return {
            attemptId: res?.attemptId ?? 0,
            scorePercent: res?.scorePercent ?? 0,
            passingScorePercent: res?.passingScorePercent ?? body.passingScorePercent ?? 80,
            passed: res?.passed ?? false,
            attemptNumber: res?.attemptNumber ?? 1,
            bestScorePercent: res?.bestScorePercent ?? 0,
            masteryPassed: res?.masteryPassed ?? false,
        }
    },

    getCourseMasterySummary: async (
        classId: string,
        stageId: string,
        courseId: string,
    ): Promise<CourseMasterySummary> => {
        const res = await enhancedRequest<any>(
            `/classes/${classId}/stages/${stageId}/courses/${courseId}/mastery/summary`,
        )
        return normalizeCourseMasterySummary(res)
    },

    getStageMasterySummary: async (classId: string, stageId: string): Promise<MasterySummary> => {
        const res = await enhancedRequest<unknown>(`/classes/${classId}/stages/${stageId}/mastery/summary`)
        return normalizeMasterySummary(res)
    },

    getClassMasterySummary: async (classId: string): Promise<MasterySummary> => {
        const res = await enhancedRequest<unknown>(`/classes/${classId}/mastery/summary`)
        return normalizeMasterySummary(res)
    },

    // ══════════════════════════════════════════════════════════
    // ADMIN — CLASSES (SchoolClass management)
    // ══════════════════════════════════════════════════════════

    getAdminClasses: async (): Promise<SchoolClass[]> => {
        const res = await enhancedRequest<any>('/admin/classes')
        const arr = Array.isArray(res) ? res : (res?.content ?? [])
        return arr.map(mapClass)
    },

    getAdminClassById: async (id: string): Promise<SchoolClass> => {
        const res = await enhancedRequest<any>(`/admin/classes/${id}`)
        return mapClass(res)
    },

    createClass: async (data: FormClass): Promise<SchoolClass> => {
        const payload = {
            title: data.title,
            description: data.description || '',
            thumbnail: data.thumbnail || '',
            slug: data.slug || '',
            targetAudience: data.targetAudience || '',
            level: data.level || null,
            language: (data.language || 'en').toLowerCase(),
            price: data.price || 0,
            isPublished: data.isPublished ?? false,
            instructor: data.instructor || { name: '', role: '', avatar: '' },
            learningOutcomes: data.learningOutcomes || [],
            prerequisites: data.prerequisites || [],
            stages: (data.stages || []).map(stage => ({
                id: stage.id,
                stageNumber: stage.stageNumber,
                title: stage.title,
                description: stage.description || '',
                courses: (stage.courses || []).map(c => ({ id: c.id })),
            })),
        }

        const res = await enhancedRequest<any>('/admin/classes', {
            method: 'POST',
            body: JSON.stringify(payload),
        })

        if (res.classId) return api.getAdminClassById(res.classId)
        throw new Error('Unexpected response from create class')
    },

    updateClass: async (id: string, data: Partial<FormClass>): Promise<SchoolClass> => {
        const payload = {
            title: data.title,
            description: data.description || '',
            thumbnail: data.thumbnail || '',
            slug: data.slug || '',
            targetAudience: data.targetAudience || '',
            level: data.level || null,
            language: (data.language || 'en').toLowerCase(),
            price: data.price || 0,
            isPublished: data.isPublished ?? false,
            instructor: data.instructor || { name: '', role: '', avatar: '' },
            learningOutcomes: data.learningOutcomes || [],
            prerequisites: data.prerequisites || [],
            stages: (data.stages || []).map(stage => ({
                id: stage.id,
                stageNumber: stage.stageNumber,
                title: stage.title,
                description: stage.description || '',
                courses: (stage.courses || []).map(c => ({ id: c.id })),
            })),
        }

        await enhancedRequest<any>(`/admin/classes/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        })

        return api.getAdminClassById(id)
    },

    deleteClass: async (id: string): Promise<void> => {
        const res = await fetch(`${API_BASE_URL}/admin/classes/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
        })
        if (res.status === 401) {
            const refreshed = await refreshAccessToken()
            if (!refreshed) throw new Error('Session expired')
            return api.deleteClass(id)
        }
        if (!res.ok) throw new Error('Delete failed')
    },

    publishClass: async (id: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${id}/publish`, { method: 'POST' }),

    unpublishClass: async (id: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${id}/unpublish`, { method: 'POST' }),

    toggleHideClass: async (id: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${id}/toggle-hidden`, { method: 'POST' }),

    // ══════════════════════════════════════════════════════════
    // ADMIN — CLASS ANALYTICS
    // ══════════════════════════════════════════════════════════

    getClassAnalytics: (classId: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${classId}/analytics`),

    getClassStudents: (classId: string, page = 0, size = 50): Promise<any> =>
        enhancedRequest(`/admin/classes/${classId}/students?page=${page}&size=${size}`),

    getClassFunnel: (classId: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${classId}/funnel`),

    getClassLessonStats: (classId: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${classId}/lessons`),

    getClassRetention: (classId: string): Promise<any> =>
        enhancedRequest(`/admin/classes/${classId}/retention`),

    // ══════════════════════════════════════════════════════════
    // COURSES — content management (admin only)
    // ══════════════════════════════════════════════════════════

    getCourseById: async (id: string, classId?: string, stageId?: string): Promise<Course> => {
        const timestamp = Date.now()
        const params = classId && stageId
            ? `?classId=${classId}&stageId=${stageId}&_t=${timestamp}`
            : `?_t=${timestamp}`
        const res = await enhancedRequest<any>(`/courses/${id}${params}`)
        return mapCourse(res)
    },

    getAdminCourses: async (): Promise<Course[]> => {
        try {
            const res = await enhancedRequest<any>('/admin/courses')
            const arr = Array.isArray(res) ? res : []
            return arr.map(mapCourse)
        } catch {
            return []
        }
    },

    getAdminCourseById: async (id: string): Promise<Course> => {
        const res = await enhancedRequest<any>(`/admin/courses/${id}`)
        return mapCourse(res)
    },

    createCourse: async (data: Partial<FormCourse>): Promise<Course> => {
        const stripIds = (obj: any): any => {
            if (!obj || typeof obj !== 'object') return obj
            if (Array.isArray(obj)) return obj.map(stripIds)
            const result: any = {}
            for (const [k, v] of Object.entries(obj)) {
                if (k === 'id') continue
                result[k] = stripIds(v)
            }
            return result
        }

        const payload = stripIds({
            title: data.title || '',
            modules: (data.modules || []).map((mod, mi) => ({
                title: mod.title,
                description: mod.description || '',
                order: mi,
                lessons: (mod.lessons || []).map((les, li) => ({
                    title: les.title,
                    type: les.type,
                    level: les.level,
                    durationMinutes: les.durationMinutes,
                    content: les.content || '',
                    videoUrl: les.videoUrl || '',
                    audioUrl: les.audioUrl || '',
                    videoId: les.videoId || undefined,
                    processingStatus: les.processingStatus || undefined,
                    resourceUrl: les.resourceUrl || '',
                    videoResources: (les.videoResources || []).map(({ name, url, premium }: { name: string; url: string; premium: boolean }) => ({ name, url, premium })),
                    interactiveData: les.practiceData || '',
                    order: li,
                    quizzes:
                        les.type === 'QUIZ'
                            ? (les.quizzes || []).map(q => ({
                                  title: q.title,
                                  questions: (q.questions || []).map(qu => ({
                                      text: qu.text,
                                      type: qu.type,
                                      options: (qu.options || []).map(o => ({ text: o.text })),
                                      correctOptionIndices: qu.correctOptionIndices || [],
                                      explanation: qu.explanation || '',
                                  })),
                              }))
                            : undefined,
                })),
            })),
        })

        const res = await enhancedRequest<any>('/admin/courses', {
            method: 'POST',
            body: JSON.stringify(payload),
        })

        if (res.courseId) return api.getAdminCourseById(res.courseId)
        throw new Error('Unexpected response from create course')
    },

    updateCourse: async (id: string, data: Partial<FormCourse>): Promise<Course> => {
        const isRealId = (val: any) =>
            typeof val === 'string' &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)

        const payload: any = {
            id,
            title: data.title || '',
            modules: (data.modules || []).map((mod, mi) => {
                const m: any = {
                    title: mod.title,
                    description: mod.description || '',
                    order: mi,
                    lessons: (mod.lessons || []).map((les, li) => {
                        const l: any = {
                            title: les.title,
                            type: les.type,
                            level: les.level,
                            durationMinutes: les.durationMinutes,
                            content: les.content || '',
                            videoUrl: les.videoUrl || '',
                    audioUrl: les.audioUrl || '',
                            videoId: les.videoId || undefined,
                            processingStatus: les.processingStatus || undefined,
                            resourceUrl: les.resourceUrl || '',
                            videoResources: (les.videoResources || []).map(({ name, url, premium }: { name: string; url: string; premium: boolean }) => ({ name, url, premium })),
                            interactiveData: les.practiceData || '',
                            order: li,
                        }
                        if (isRealId(les.id)) l.id = les.id
                        if (les.type === 'QUIZ' && les.quizzes) {
                            l.quizzes = les.quizzes.map(q => {
                                const qd: any = {
                                    title: q.title,
                                    questions: (q.questions || []).map(qu => {
                                        const qud: any = {
                                            text: qu.text,
                                            type: qu.type,
                                            options: (qu.options || []).map(o => {
                                                const od: any = { text: o.text }
                                                if (isRealId(o.id)) od.id = o.id
                                                return od
                                            }),
                                            correctOptionIndices: qu.correctOptionIndices || [],
                                            explanation: qu.explanation || '',
                                        }
                                        if (isRealId(qu.id)) qud.id = qu.id
                                        return qud
                                    }),
                                }
                                if (isRealId(q.id)) qd.id = q.id
                                return qd
                            })
                        }
                        return l
                    }),
                }
                if (isRealId(mod.id)) m.id = mod.id
                return m
            }),
        }

        await enhancedRequest<any>(`/admin/courses/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        })

        return api.getAdminCourseById(id)
    },

    deleteCourse: async (id: string): Promise<void> => {
        const res = await fetch(`${API_BASE_URL}/admin/courses/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
        })
        if (res.status === 401) {
            const refreshed = await refreshAccessToken()
            if (!refreshed) throw new Error('Session expired')
            return api.deleteCourse(id)
        }
        if (!res.ok) throw new Error('Delete failed')
    },

    // ══════════════════════════════════════════════════════════
    // USERS
    // ══════════════════════════════════════════════════════════

    updateProfile: (data: { firstName?: string; lastName?: string; preferredLanguage?: string }): Promise<any> =>
        enhancedRequest('/users/me', {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),

    // Delete own account (for regular users)
    deleteAccount: (): Promise<any> =>
        enhancedRequest('/users/me', { method: 'DELETE' }),

    exportUserData: (): Promise<any> =>
        enhancedRequest('/users/me/export'),

    // ══════════════════════════════════════════════════════════
    // ADMIN — USERS (FIXED!)
    // ══════════════════════════════════════════════════════════

    getUsers: async (page = 0, size = 50): Promise<{
        users: User[]
        totalPages: number
        totalElements: number
        currentPage: number
        size: number
        hasNext: boolean
    }> => {
        try {
            const res = await enhancedRequest<any>(`/admin/users?page=${page}&size=${size}`)
            
            // Handle paginated response
            if (!res || !Array.isArray(res.users)) {
                return {
                    users: [],
                    totalPages: 0,
                    totalElements: 0,
                    currentPage: 0,
                    size: 0,
                    hasNext: false,
                }
            }
            
            return {
                users: res.users.map((u: any) => ({
                    id: u.id?.toString() || '',
                    name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
                    email: u.email || '',
                    firstName: u.firstName || '',
                    lastName: u.lastName || '',
                    role: u.role?.toLowerCase() === 'admin' ? 'admin' : 'student',
                    enabled: u.enabled ?? true,
                    createdAt: u.createdAt,
                    updatedAt: u.updatedAt,
                    hasActiveSubscription: u.hasActiveSubscription ?? false,
                    subscriptionTier: u.subscriptionTier || 'FREE',
                    subscriptionExpiresAt: u.subscriptionExpiresAt,
                    provider: u.provider,
                    picture: u.picture,
                    phoneNumber: u.phoneNumber,
                })),
                totalPages: res.totalPages || 0,
                totalElements: res.totalElements || 0,
                currentPage: res.currentPage || 0,
                size: res.size || 0,
                hasNext: res.hasNext || false,
            }
        } catch {
            return {
                users: [],
                totalPages: 0,
                totalElements: 0,
                currentPage: 0,
                size: 0,
                hasNext: false,
            }
        }
    },

    // FIXED: Now uses enhancedRequest with proper credentials and auth
    getUserById: async (userId: string): Promise<User> => {
        const res = await enhancedRequest<any>(`/admin/users/${userId}`)

        // Normalize to User type (same shape as getUsers)
        return {
            reviewer: res.reviewer === true,
            reviewClassIds: res.reviewClassIds ?? [],
            id: res.id?.toString() || '',
            name: `${res.firstName || ''} ${res.lastName || ''}`.trim() || res.email,
            email: res.email || '',
            firstName: res.firstName || '',
            lastName: res.lastName || '',
            role: res.role?.toLowerCase() === 'admin' ? 'admin' : 'student',
            enabled: res.enabled ?? true,
            createdAt: res.createdAt,
            updatedAt: res.updatedAt,
            hasActiveSubscription: res.hasActiveSubscription ?? false,
            subscriptionTier: res.subscriptionTier || 'FREE',
            subscriptionExpiresAt: res.subscriptionExpiresAt,
            subscriptionBillingCycle: res.subscriptionBillingCycle,
            provider: res.provider,
            picture: res.picture,
            phoneNumber: res.phoneNumber,
        }
    },

    deleteUser: (id: string): Promise<any> =>
        enhancedRequest(`/admin/users/${id}`, { method: 'DELETE' }),

    blockUser: (id: string): Promise<any> =>
        enhancedRequest(`/admin/users/${id}/block`, { method: 'PATCH' }),

    unblockUser: (id: string): Promise<any> =>
        enhancedRequest(`/admin/users/${id}/unblock`, { method: 'PATCH' }),

    // ══════════════════════════════════════════════════════════
    // ADMIN — STUDENT ANALYTICS (FIXED!)
    // ══════════════════════════════════════════════════════════

    getStudentClasses: async (studentId: string): Promise<any[]> => {
        const res = await enhancedRequest<any>(`/admin/students/${studentId}/classes`)
        return Array.isArray(res) ? res : []
    },

    // FIXED: Now uses enhancedRequest
    getStudentAnalytics: async (
        studentId: string
    ): Promise<{
        totalLessonsCompleted: number
        totalLearningSeconds: number
        enrolledClasses: number
        completedClasses: number
    }> => {
        return enhancedRequest(`/admin/students/${studentId}/analytics`)
    },

    revokeStudentEnrollment: (userId: string, classId: string): Promise<void> =>
        enhancedRequest(`/admin/students/${userId}/enrollments/${classId}`, { method: 'DELETE' }),

    // ══════════════════════════════════════════════════════════
    // ADMIN — WHITELIST
    // ══════════════════════════════════════════════════════════

    getAdminWhitelist: async (): Promise<WhitelistedEmailEntry[]> => {
        const res = await enhancedRequest<WhitelistedEmailEntry[]>('/admin/whitelist')
        return Array.isArray(res) ? res : []
    },

    addAdminWhitelistEmail: async (
        email: string,
        note?: string,
    ): Promise<WhitelistedEmailEntry> =>
        enhancedRequest('/admin/whitelist', {
            method: 'POST',
            body: JSON.stringify({ email, note: note ?? '' }),
        }),

    removeAdminWhitelistEmail: (id: string): Promise<void> =>
        enhancedRequest(`/admin/whitelist/${id}`, { method: 'DELETE' }),

    // ══════════════════════════════════════════════════════════
    // AUTH & SUBSCRIPTION
    // ══════════════════════════════════════════════════════════

    checkSubscription: (): Promise<{
        hasActiveSubscription: boolean
        expiresAt: string
        tier: string
    }> => enhancedRequest('/auth/subscription'),

    // Mints a short-lived, single-use code the Flutter app trades for a real
    // session (see /auth/mobile-handoff/exchange). Only called when the current
    // signup/checkout session started as a "Create account on the web" handoff
    // from the mobile app — see dhtn_auth_origin in PaymentCallbackPage.
    createMobileHandoffCode: (): Promise<{ code: string }> =>
        enhancedRequest('/auth/mobile-handoff', { method: 'POST' }),

    exchangeOAuthCode: async (code: string): Promise<{ accessToken: string; expiresIn: number }> => {
        const res = await fetch(`${API_BASE_URL}/auth/token-exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
        })
        if (!res.ok) {
            const err = new Error('OAuth code exchange failed')
            err.name = 'AuthError'
            throw err
        }
        return res.json()
    },

    // ══════════════════════════════════════════════════════════
    // ANALYTICS
    // ══════════════════════════════════════════════════════════

    getDashboardStats: (): Promise<{
        totalClasses: number
        totalStudents: number
        totalEnrollments: number
        completionRate: number
        recentEnrollments: number
    }> => enhancedRequest('/dashboard/stats'),

    getUserAnalytics: async (options: { background?: boolean } = {}): Promise<any> => {
        const res = await enhancedRequest<any>('/analytics/user', options)
        // Backend wraps in ApiResponse<UserAnalytics>: { success, message, data, error }
        return res?.data ?? res
    },

    getMonthlyAnalytics: async (year: number, month: number): Promise<MonthlyAnalytics> => {
        const res = await enhancedRequest<{ data: MonthlyAnalytics }>(`/analytics/monthly?year=${year}&month=${month}`)
        return (res?.data ?? res) as MonthlyAnalytics
    },

    // Last N aligned weeks (Mon–Sun), one entry per day — powers the dashboard's 2-week row.
    getRecentWeeks: async (
        weeks = 2,
    ): Promise<Array<{ day: string; date: string; isToday: boolean; seconds: number; lessonsCompleted: number }>> => {
        const res = await enhancedRequest<{ data: unknown }>(`/analytics/recent-weeks?weeks=${weeks}`)
        return (res?.data ?? res) as Array<{ day: string; date: string; isToday: boolean; seconds: number; lessonsCompleted: number }>
    },

    // ══════════════════════════════════════════════════════════
    // PAYMENT
    // ══════════════════════════════════════════════════════════

    initializePayment: (plan: 'FULL_ACCESS_MONTHLY' | 'FULL_ACCESS_YEARLY'): Promise<{
        authorizationUrl: string
        reference: string
        accessCode: string
    }> => enhancedRequest('/payment/initialize', {
        method: 'POST',
        body: JSON.stringify({ plan }),
    }),

    verifyPayment: (reference: string): Promise<{
        success: boolean
        message: string
    }> => enhancedRequest(`/payment/verify/${encodeURIComponent(reference)}`, {
        method: 'POST',
    }),

    // ══════════════════════════════════════════════════════════
    // SESSION MANAGEMENT
    // ══════════════════════════════════════════════════════════

    getSessions: async (): Promise<Array<{
        sessionId: string
        device: string
        location: string
        lastActive: string
        current: boolean
    }>> => {
        const data = await enhancedRequest('/auth/sessions')
        return Array.isArray(data) ? data : ((data as any)?.sessions ?? [])
    },

    revokeSession: (sessionId: string): Promise<void> =>
        enhancedRequest(`/auth/sessions/${sessionId}`, { method: 'DELETE' }),

    revokeAllOtherSessions: (): Promise<void> =>
        enhancedRequest('/auth/sessions', { method: 'DELETE' }),

    // ══════════════════════════════════════════════════════════
    // ANNOUNCEMENTS
    // ══════════════════════════════════════════════════════════

    /** Admin: send an announcement via email, WhatsApp, or both. */
    sendAnnouncement: async (payload: {
        subject: string
        body: string
        targetType: 'ALL' | 'CLASS' | 'WAITLIST'
        targetClassId?: string
        targetClassName?: string
        templateType?: 'general' | 'important' | 'new_resource' | 'celebration'
        channels?: 'EMAIL' | 'WHATSAPP' | 'BOTH'
    }): Promise<Announcement> => {
        return request('/admin/announcements', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
    },

    /** Public: fetch the active announcement banner (null if none active). */
    getActiveBanner: async (): Promise<AppBanner | null> => {
        try {
            const data = await request<AppBanner>('/app/banner')
            return data && data.id ? data : null
        } catch (err) {
            console.error('[banner] Failed to fetch active banner:', err)
            return null
        }
    },

    /** Public: fetch the current Android release (version + download URL), null if unavailable. */
    getLatestApk: async (): Promise<AppRelease | null> => {
        try {
            const data = await request<AppRelease>('/app/latest-apk')
            return data && data.downloadUrl ? data : null
        } catch (err) {
            console.error('[latest-apk] Failed to fetch latest APK info:', err)
            return null
        }
    },

    /** Admin: create or replace the active banner. */
    createBanner: async (payload: { message: string; ctaText?: string; ctaUrl?: string }): Promise<AppBanner> => {
        return request<AppBanner>('/admin/app/banner', {
            method: 'POST',
            body: JSON.stringify(payload),
        })
    },

    /** Admin: deactivate a banner by ID. */
    deactivateBanner: async (id: string): Promise<void> => {
        await request(`/admin/app/banner/${id}`, { method: 'DELETE' })
    },

    /** Admin: fetch announcement history, newest first. */
    getAnnouncements: async (): Promise<Announcement[]> => {
        const data: { announcements: Announcement[] } = await request('/admin/announcements')
        return data.announcements ?? []
    },

    deleteAnnouncement: async (id: string): Promise<void> => {
        await request(`/admin/announcements/${id}`, { method: 'DELETE' })
    },

    /** Admin: get count of users who have opted in to WhatsApp notifications. */
    getWhatsappOptInCount: async (): Promise<number> => {
        const data: { count: number } = await request('/admin/announcements/whatsapp-count')
        return data.count ?? 0
    },

    /** Student: update WhatsApp phone number and opt-in preference. */
    updateWhatsappSettings: async (data: {
        phoneNumber: string
        whatsappOptIn: boolean
    }): Promise<{ message: string; user: unknown }> => {
        return request('/users/me/whatsapp', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        })
    },

    // ══════════════════════════════════════════════════════════
    // PREFERENCES
    // ══════════════════════════════════════════════════════════

    /** One-click unsubscribe from marketing emails. No auth required. */
    unsubscribe: async (token: string): Promise<{ message: string }> => {
        const res = await fetch(`${API_BASE_URL}/unsubscribe?token=${encodeURIComponent(token)}`)
        const data = await res.json()
        if (!res.ok) throw new Error(data.error ?? 'Unsubscribe failed')
        return data
    },

    // ══════════════════════════════════════════════════════════
    // MEDIA — Bunny.net Storage
    // ══════════════════════════════════════════════════════════

    /**
     * Admin: upload MP4 directly to Bunny Stream via TUS protocol.
     * The backend only issues a signed token — video bytes go Browser → Bunny CDN directly,
     * bypassing Railway entirely so proxy timeouts are impossible regardless of file size.
     * Supports automatic resume if the connection drops mid-upload.
     */
    uploadVideo: async (file: File, onProgress: (pct: number) => void): Promise<{ videoId: string; videoUrl: string; processingStatus: string }> => {
        const init: {
            videoId: string; videoUrl: string; processingStatus: string
            tusEndpoint: string; tusSignature: string; tusExpiry: string; libraryId: string
        } = await request('/admin/media/video/init', { method: 'POST' })

        onProgress(0)

        const { Upload } = await import('tus-js-client')
        return new Promise((resolve, reject) => {
            const upload = new Upload(file, {
                endpoint: init.tusEndpoint,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                chunkSize: 50 * 1024 * 1024, // 50 MB per TUS chunk — fast, still resumable
                headers: {
                    AuthorizationSignature: init.tusSignature,
                    AuthorizationExpire:    init.tusExpiry,
                    VideoId:                init.videoId,
                    LibraryId:              init.libraryId,
                },
                metadata: {
                    filetype: file.type,
                    title:    file.name,
                },
                onProgress: (uploaded, total) => {
                    onProgress(Math.round((uploaded / total) * 90))
                },
                onSuccess: () => {
                    resolve({ videoId: init.videoId, videoUrl: init.videoUrl, processingStatus: 'processing' })
                },
                onError: (err) => {
                    reject(new Error(String(err)))
                },
            })
            upload.start()
        })
    },

    /** Admin: poll Bunny Stream encoding status. Returns {processingStatus: "processing"|"ready"|"failed"}. */
    getVideoStatus: async (videoId: string): Promise<{ processingStatus: string }> =>
        request(`/admin/media/video/${videoId}/status`),

    getLessonPlayback: async (
        lessonId: string,
        classId: string,
        stageId: string
    ): Promise<LessonPlayback> =>
        enhancedRequest(
            `/media/lessons/${encodeURIComponent(lessonId)}/playback?classId=${encodeURIComponent(classId)}&stageId=${encodeURIComponent(stageId)}`
        ),

    /** Admin: upload audio file to Bunny Storage. Returns {audioUrl}. */
    uploadAudio: async (file: File): Promise<{ audioUrl: string }> => {
        const formData = new FormData()
        formData.append('file', file)
        return request('/admin/media/audio', { method: 'POST', body: formData })
    },

    uploadDocument: async (file: File): Promise<{ resourceUrl: string }> => {
        const formData = new FormData()
        formData.append('file', file)
        return request('/admin/media/document', { method: 'POST', body: formData })
    },

    /** Admin: upload a cover image (PNG/JPEG/WebP) to Bunny Storage. Returns {imageUrl}.
     *  `folder` (the class name/slug) organizes the file under /images/{slug}/ on the CDN. */
    uploadImage: async (file: File, folder?: string): Promise<{ imageUrl: string }> => {
        const formData = new FormData()
        formData.append('file', file)
        const qs = folder && folder.trim() ? `?folder=${encodeURIComponent(folder.trim())}&purpose=cover` : ''
        return request(`/admin/media/image${qs}`, { method: 'POST', body: formData })
    },

    /**
     * Admin: organize every course's existing Bunny Stream videos into one collection per course.
     * Idempotent — safe to re-run. Returns a summary of what was organized.
     */
    organizeVideoCollections: async (): Promise<{ coursesProcessed: number; collectionsCreated: number; videosOrganized: number }> =>
        request('/admin/media/collections/sync', { method: 'POST' }),

    // ══════════════════════════════════════════════════════════
    // ACHIEVEMENTS
    // ══════════════════════════════════════════════════════════

    getCertificates: async (): Promise<CertificateDTO[]> => {
        const data = await enhancedRequest<any>('/achievements/certificates')
        return Array.isArray(data) ? data : (data?.certificates ?? [])
    },

    recordCertificateDownload: async (certId: string): Promise<CertificateDTO> =>
        enhancedRequest(`/achievements/certificates/${certId}/download`, { method: 'POST' }),

    verifyCertificate: async (certNumber: string): Promise<CertificateDTO> =>
        enhancedRequest<CertificateDTO>(`/achievements/verify/${encodeURIComponent(certNumber)}`),

    // TOKEN MANAGEMENT
    // ══════════════════════════════════════════════════════════

    setAccessToken: (token: string, expiryMs: number) => setAccessToken(token, expiryMs),
    setRefreshToken: (token: string) => setRefreshToken(token),
    getRefreshToken: () => getRefreshToken(),
    clearTokens: () => clearTokens(),
    refreshAccessToken: () => refreshAccessToken(),
    getAccessToken: () => getAccessToken(),
}
